/**
 * PDFGenerator Component
 * Main component for generating and downloading PDF documents
 */

import React from 'react';
import { PDFDownloadLink } from '@react-pdf/renderer';
import { useTranslation } from 'react-i18next';
import { FiDownload } from 'react-icons/fi';
import { PDFGeneratorProps } from './types';
import { usePDFData } from './hooks';
import { PDFDocumentContent } from './components';

const PDFGenerator: React.FC<PDFGeneratorProps> = ({ sessionId, className = '' }) => {
  const { t } = useTranslation(['training', 'common']);
  const { sessionData, isLoading, error, refetch } = usePDFData(sessionId);

  if (isLoading) {
    return (
      <button
        disabled
        className={`flex items-center justify-center px-4 py-2 bg-muted text-muted-foreground rounded-md cursor-not-allowed ${className}`}
      >
        <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
        {t('common:loading.loading') as string}
      </button>
    );
  }

  if (error || !sessionData) {
    return (
      <button
        onClick={refetch}
        className={`flex items-center justify-center px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors ${className}`}
      >
        <FiDownload className="mr-2" />
        {t('common:common.retry') as string}
      </button>
    );
  }

  const currentDate = new Date().toISOString().split('T')[0]; // YYYY-MM-DD

  // Sanitize session name for filename (remove special characters, replace spaces with underscores)
  const sanitizeFilename = (name: string): string => {
    return name
      .replace(/[^a-zA-Z0-9äöüÄÖÜß\s-]/g, '') // Keep alphanumeric, German chars, spaces, hyphens
      .replace(/\s+/g, '_') // Replace spaces with underscores
      .substring(0, 50); // Limit length
  };

  const sessionName = sessionData.sessionName
    ? sanitizeFilename(sessionData.sessionName)
    : sessionId;

  return (
    <PDFDownloadLink
      document={<PDFDocumentContent sessionId={sessionId} data={sessionData} />}
      fileName={`${currentDate}-${sessionName}.pdf`}
      className={`flex items-center justify-center px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 transition-colors ${className}`}
    >
      {({ loading }) => (
        <>
          {loading ? (
            <span className="inline-block w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
          ) : (
            <FiDownload className="mr-2" />
          )}
          {t('training.buttons.pdfGenerate')}
        </>
      )}
    </PDFDownloadLink>
  );
};

export default PDFGenerator;
